import { FC, useState, useEffect } from 'react';
import { Button, Input, Row, Col, Typography } from 'antd';
import HalfScreenModal from 'components/HalfScreenModal/HalfScreenModal';
import FixedAlertMessage from 'components/FixedAlert/FixedAlertMessage';

import styles from './editCustomPageTitle.module.less';

const { Text } = Typography;

interface Props {
  visible: boolean;
  onClose: () => void;
  onSave: (title: string) => void;
  onCheckTitleExists: (title: string) => Promise<boolean>;
  initialTitle?: string;
  textLimit?: number;
}

const EditCustomPageTitle: FC<Props> = (props: Props) => {
  const {
    visible,
    onClose,
    onSave,
    onCheckTitleExists,
    initialTitle = 'This is a test',
    textLimit = 22,
  } = props;

  const [text, setText] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [titleExistsError, setTitleExistsError] = useState<boolean>(false);

  useEffect(() => {
    if (visible) {
      setText(initialTitle);
      setTitleExistsError(false);
    }
  }, [visible, initialTitle]);

  const handleSave = async () => {
    setIsLoading(true);
    setTitleExistsError(false);

    try {
      // Check if title already exists
      const titleExists = await onCheckTitleExists(text.trim());

      if (titleExists) {
        setTitleExistsError(true);
        setIsLoading(false);
        return;
      }
      await onSave(text.trim());
      onClose();
    } catch (error) {
      console.error('Error saving title:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setText(initialTitle);
    setTitleExistsError(false);
    onClose();
  };

  const textCharacterLength = text.length;
  const isDisabled =
    textCharacterLength > textLimit || !text.trim().length || titleExistsError;

  return (
    <HalfScreenModal
      width="552px"
      title={`Edit ${initialTitle} Title`}
      visible={visible}
      className={styles.modalWrapper}
      footer={[
        <Button
          className={`${styles.btn} ${styles.cancel}`}
          key="cancel"
          onClick={handleClose}
          loading={isLoading}
        >
          Cancel
        </Button>,
        <Button
          className={styles.btn}
          key="submit"
          type="primary"
          onClick={handleSave}
          disabled={isDisabled}
          loading={isLoading}
        >
          Done
        </Button>,
      ]}
      onCancel={handleClose}
    >
      <FixedAlertMessage
        message="Changing the navigation title will also change the page title. Please update with caution."
        type="error"
      />
      <Col xs={24} className={styles.labelWrapper}>
        <div>New Title *</div>
      </Col>
      <Row>
        <Col xs={24}>
          <Input
            className={`${styles.input} ${
              textCharacterLength > textLimit || titleExistsError
                ? styles.errorInput
                : ''
            }`}
            placeholder="Custom Title goes here..."
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              if (titleExistsError) {
                setTitleExistsError(false);
              }
            }}
            maxLength={100}
          />
        </Col>
      </Row>
      <Row justify="space-between">
        <Text
          className={`${styles.charLimit} ${
            textCharacterLength > textLimit || titleExistsError
              ? styles.errorText
              : ''
          }`}
        >
          {titleExistsError
            ? 'Title already exists'
            : textCharacterLength > textLimit
            ? 'Character Limit reached'
            : ''}
        </Text>
        <Text
          className={`${styles.charLimit} ${
            textCharacterLength > textLimit ? styles.errorText : ''
          }`}
        >
          Character Limit: {textCharacterLength}/{textLimit}
        </Text>
      </Row>
    </HalfScreenModal>
  );
};

export default EditCustomPageTitle;
